"use client";

import React from "react";
import type { MatchingResponse } from "@/lib/api/kundali";
import Explainable from "@ishvaram/ui/Explainable";
import { useExplainableHandlers, useExplainer } from "@/hooks/useExplainer";
import type { CustomerLanguage } from "@/lib/customer-language";
import { matchingCopy } from "./matching-copy";

// Koota bar colors by score ratio
function kootaColor(score: number, max: number): string {
  const ratio = score / max;
  if (ratio >= 0.75) return "var(--color-success)";
  if (ratio >= 0.5) return "var(--color-warning)";
  if (ratio > 0) return "var(--color-warning-orange)";
  return "var(--color-error-text)";
}

// Map API koota display name → explainers.yaml id. Keep in sync with
// business/ishvaram/data/ai/explainers_catalog.tsv koota rows.
const KOOTA_NAME_TO_ID: Record<string, string> = {
  Varna: "varna",
  Vashya: "vashya",
  Tara: "tara",
  Yoni: "yoni",
  "Graha Maitri": "graha_maitri",
  Gana: "gana",
  Bhakoot: "bhakoot",
  Nadi: "nadi",
};

export function KootaBreakdown({
  result,
  language,
}: {
  // Narrowed to the ONE field this reads, so the naam-akshar result — which has
  // no mangal_dosha and no person details — can render the same eight kootas
  // without a second copy of this UI (rules 23/24). Type-level only; the runtime
  // behaviour is unchanged.
  result: Pick<MatchingResponse, "kootas">;
  language: CustomerLanguage;
}) {
  const copy = matchingCopy(language);
  return (
    <div className="card-sacred min-h-[560px] p-6 sm:p-8">
      <h2 className="text-[18px] font-bold mb-5 text-ish-dark font-serif">{copy.kootaBreakdown}</h2>
      <div className="space-y-4">
        {result.kootas.map((koota) => (
          <KootaRow key={koota.name} koota={koota} language={language} />
        ))}
      </div>
    </div>
  );
}

function KootaRow({
  koota,
  language,
}: {
  koota: { name: string; name_hi: string; score: number; max: number; description?: string };
  language: CustomerLanguage;
}) {
  const id = KOOTA_NAME_TO_ID[koota.name];
  const intensity: "low" | "medium" | "high" =
    koota.score === koota.max ? "low" : koota.score === 0 ? "high" : "medium";
  const handlers = useExplainableHandlers("gun_milan_result");
  const { data, isLoading, error, refetch } = useExplainer("koota", id ?? "varna", intensity, {
    enabled: false,
  });

  const scoreText = `${koota.score}/${koota.max}`;
  // Hindi pages lead with the Hindi koota name and keep the Latin one as the gloss.
  const [label, gloss] =
    language === "hi" && koota.name_hi ? [koota.name_hi, koota.name] : [koota.name, koota.name_hi];
  const scoreColor = kootaColor(koota.score, koota.max);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          {id ? (
            <Explainable
              type="koota"
              id={id}
              intensity={intensity}
              triggerLabel={label}
              data={data}
              loading={isLoading}
              error={error ?? null}
              onRequestData={() => void refetch()}
              {...handlers}
              className="text-body font-semibold"
            />
          ) : (
            <span className="text-body font-semibold text-ish-dark">{label}</span>
          )}
          {gloss ? <span className="text-caption text-ish-text-faint">({gloss})</span> : null}
        </div>
        {id ? (
          // The colored score is the #1 dead-click target (Clarity 2026-05-17,
          // rule 32): seekers tap "0/8" expecting "why?". Share the row's
          // explainer so the score opens the same cultural-prose + upay sheet.
          <Explainable
            type="koota"
            id={id}
            intensity={intensity}
            triggerLabel={`${label}: ${scoreText}`}
            data={data}
            loading={isLoading}
            error={error ?? null}
            onRequestData={() => void refetch()}
            {...handlers}
            className="text-body font-bold"
          >
            <span style={{ color: scoreColor, textDecoration: "underline dotted" }}>
              {scoreText}
            </span>
          </Explainable>
        ) : (
          // inert-by-design: koota name has no explainers.yaml id mapping, so
          // there is no sheet to open for this score (no cultural prose exists).
          <span className="text-body font-bold" style={{ color: scoreColor }}>
            {scoreText}
          </span>
        )}
      </div>
      <div className="w-full h-2 rounded-full overflow-hidden bg-ish-border">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${(koota.score / koota.max) * 100}%`,
            backgroundColor: kootaColor(koota.score, koota.max),
            minWidth: koota.score > 0 ? "4px" : "0",
          }}
        />
      </div>
      {koota.description ? (
        <p className="text-caption mt-1 text-ish-text-muted">{koota.description}</p>
      ) : null}
    </div>
  );
}
